import useAPI from '../hooks/useAPI.js';
import React, { lazy, Suspense, useCallback, useMemo } from 'react';
import ErrorBoundary from '../modules/errors/Boundary.js';
import { DrawingZone } from 'components/DrawingZone';
import Fullscreen from './Fullscreen.js';
import ExclusiveContent from './ExclusiveContent/index.js';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

const sharedComponents = {
  DrawingZone,
  Fullscreen,
  ExclusiveContent,
};

const builtInExtensions = new Set([
  'emoji',
  'poll',
  'quiz',
  'promo-landing'
]);

const selectExtensions = (state) => state.extensions;
const selectAllowedExtensions = createSelector(
  selectExtensions,
  (extensions) =>
    extensions.filter(
      ({ allowed, enabled, name }) => (allowed || name === 'emoji') && enabled,
    ),
);
const selectBarneyExtensions = createSelector(
  selectExtensions,
  (extensions) =>
    extensions.filter(
      ({ barneyMode, allowed, enabled }) => !allowed && barneyMode && enabled,
    ),
);

function BuiltInLoader(extension) {
  return () => import(`../extensions/_${extension.name}/index.tsx`);
}

function ExternalLoader(extension, dependencies) {
  return () => {

    /* Exposing React for extensions, at the moment the only one that required this, "debug-layer".
    * TODO: Find other approach. */
    window.React = React;

    return import(/* @vite-ignore */ extension.src).then((_module) => {
      console.log('module', _module);
      try {
        const { Factory } = window.InteractiveExtension[extension.name];
        const implementation = Factory(extension, dependencies);
        return {
          default: (props) => (
            <implementation.UI UI={sharedComponents} {...props} />
          ),
        };
      } catch (error) {
        console.warn('Error loading external extension', extension.name, error);
      }
    });
  };
}
/**
 * @param {React.ReactPropTypes} props
 * @param {[Object]} props.extensions
 * @return {[React.ReactElement]}
 * @todo Найти решение лучше чем прокидывать внутрь каждого расширения  DrawingZone (и/или все остальные компоненты которые могут быть там нужны).
 *    Варианты:
 *    - определять обёртки, контексты и т.д. на основе описания расширения и применять здесь
 *    - определять и передавать в расширение
 *    - применить _как-то_ инверсию и предоставить в распоряжение каждого расширения все базовые возможности/компоненты интерактива
 *    - еще что-то
 */
export function Extensions(props) {
  const API = useAPI();

  const loader = useMemo(() => {
    const extensions = new Map();
    return {
      get(extension) {
        if (!extensions.has(extension.name)) {
          const loader = builtInExtensions.has(extension.name) ?
            new BuiltInLoader(extension) :
            new ExternalLoader(extension, { API });

          extensions.set(extension.name, lazy(loader));
        }
        return extensions.get(extension.name);
      }
    };
  }, [API]);

  const onError = useCallback((error) => {
    console.error(error);
  }, []);

  const onMountAllowedExtension = useCallback((name, impl) => {
    return API.renderersRegistry.register(name, impl);
  }, [API.renderersRegistry]);

  const onMountBarneyExtension = useCallback((name, impl) => {
    return API.renderersRegistry.register(name, {
      test: function (schema) {
        return (
          impl.test(schema) && true === schema.isIgnoreDisallowedPlayerSetting
        );
      },
    });
  }, [API.renderersRegistry]);

  return (
    <>
      <ExtensionsList
        loader={loader}
        selector={selectAllowedExtensions}
        currentObject={props.currentObject}
        onError={onError}
        onMount={onMountAllowedExtension}
      />
      <ExtensionsList
        loader={loader}
        selector={selectBarneyExtensions}
        currentObject={props.currentObject}
        onError={onError}
        onMount={onMountBarneyExtension}
      />
    </>
  );
}

function ExtensionsList({
  selector,
  currentObject,
  onError,
  onMount,
  loader
}) {
  const extensions = useSelector(selector);

  return extensions.map((extension) => {
    const {
      options,
      enabled,
      allowed,
    } = extension;
    const Extension = loader.get(extension);

    return (
      <ErrorBoundary onError={onError} key={extension.name}>
        <Suspense fallback={null}>
          <Extension
            enabled={enabled}
            allowed={allowed}
            options={options}
            currentObject={currentObject}
            onMount={onMount}
          />
        </Suspense>
      </ErrorBoundary>
    );
  });
}
